import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostBinding,
  inject,
  signal,
} from '@angular/core';
import {
  AiFeaturesService,
  ALL_HOUSEHOLDS_THREAD_SCOPE,
  DEFAULT_LANGUAGE,
  IQBOOST_CONVERSATION_STORE,
  IQBoostAllHouseholdsStore,
  LANGFUSE_SYSTEM_TAG,
  LangfuseResponseExtensionSuggestedPrompt,
  LanguageHelper,
  MaximizerAiService,
  SYSTEM_PROMPT_SUGGESTED_PROMPTS_ALL_HOUSEHOLDS,
} from '@maximizer/ai/data-access';
import { EmbeddedComponent } from '@maximizer/core/shared/ui/components/base';
import { ActionEventType, OpenEntityArgs } from '@maximizer/core/shared/ui/config';
import { isNullOrEmpty } from '@maximizer/shared/util';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, finalize, map, Observable, of, tap } from 'rxjs';
import { IQBoostTextVersion } from '../../classes/iqboost';
import { IqboostChatDialogComponent } from '../iqboost-chat-dialog/iqboost-chat-dialog.component';
import { IQBoostChatScope } from '../iqboost-chat-scope';
import { IQBoostStarterPrompt } from '../iqboost-starter-prompts/iqboost-starter-prompts.component';

/** Shown on a starter card whose Langfuse entry names no icon. */
export const ALL_HOUSEHOLDS_DEFAULT_ICON_CLASS = 'fa-icons icon-light icon-users';

const ALL_HOUSEHOLDS_SCOPE: IQBoostChatScope = {
  mode: 'all',
  thread: ALL_HOUSEHOLDS_THREAD_SCOPE,
  instruction: '',
};

// Only a string names a language; an entry whose `type` is anything else is never offered.
const inLanguage = (
  prompts: LangfuseResponseExtensionSuggestedPrompt[],
  language: string,
): LangfuseResponseExtensionSuggestedPrompt[] =>
  prompts.filter(
    (prompt) => typeof prompt.type === 'string' && prompt.type.toLowerCase() === language,
  );

/**
 * The entries written in the reader's language, or in English when there are none; each asked as
 * it reads. The icon rides on the entry, as the single-household catalog's does on its category.
 */
export const toAllHouseholdsStarterPrompts = (
  suggestedPrompts: LangfuseResponseExtensionSuggestedPrompt[],
  language = LanguageHelper.language(),
): IQBoostStarterPrompt[] => {
  const worded = suggestedPrompts.filter((prompt) => !isNullOrEmpty(prompt.text));
  const localized = inLanguage(worded, language.toLowerCase());
  const chosen = localized.length ? localized : inLanguage(worded, DEFAULT_LANGUAGE);
  return chosen.map(({ text, iconClass }) => ({
    text,
    iconClass: iconClass || ALL_HOUSEHOLDS_DEFAULT_ICON_CLASS,
  }));
};

@Component({
  selector: 'ai-iqboost-all-households',
  templateUrl: './iqboost-all-households.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: IQBOOST_CONVERSATION_STORE, useExisting: IQBoostAllHouseholdsStore }],
  imports: [IqboostChatDialogComponent, TranslatePipe],
})
export class IqboostAllHouseholdsComponent extends EmbeddedComponent<OpenEntityArgs> {
  @HostBinding('class') hostClasses = 'ai-ng ai-headless-ng';

  private readonly aiFeatures = inject(AiFeaturesService);
  private readonly maximizerAi = inject(MaximizerAiService);

  readonly isOpen = signal(false);
  // Kept from one opening to the next.
  readonly richUi = signal(true);
  readonly prompts = signal<IQBoostStarterPrompt[]>([]);
  readonly loadingPrompts = signal(false);

  protected readonly scope = ALL_HOUSEHOLDS_SCOPE;
  protected readonly textVersion = computed(() =>
    this.aiFeatures.isSLE()
      ? IQBoostTextVersion.defaultVersion
      : IQBoostTextVersion.faVersion,
  );
  protected readonly scopeKey = computed(
    () => `iqboost.allHouseholds.scope.${this.textVersion()}`,
  );
  protected readonly subtitleKey = computed(
    () => `iqboost.allHouseholds.subtitle.${this.textVersion()}`,
  );
  protected readonly placeholderKey = computed(
    () => `iqboost.allHouseholds.placeholder.${this.textVersion()}`,
  );

  // Looked up once, on the first opening: the prompts do not change while the host page is open.
  private promptsLoaded = false;

  constructor() {
    super(inject(ElementRef));
  }

  override doAction(name: string): void {
    if (name === ActionEventType.Show || name === ActionEventType.Initialize) {
      this.isOpen.set(true);
      this.loadPrompts();
    } else {
      super.doAction(name, undefined);
    }
  }

  // Without the Langfuse entry the landing view offers no prompts; the question box still works.
  private loadPrompts(): void {
    if (this.promptsLoaded) {
      return;
    }
    this.promptsLoaded = true;
    this.loadingPrompts.set(true);
    this.addSubscription(this.lookupStarterPrompts().subscribe());
  }

  private lookupStarterPrompts(): Observable<IQBoostStarterPrompt[]> {
    return this.maximizerAi
      .lookupPrompts({
        project: this.aiFeatures.isSLE() ? 'IqBoostSLE' : 'IqBoost',
        filter: {
          tag: [LANGFUSE_SYSTEM_TAG],
          name: [SYSTEM_PROMPT_SUGGESTED_PROMPTS_ALL_HOUSEHOLDS],
        },
      })
      .pipe(
        map(
          (prompts) =>
            prompts.find((prompt) => prompt.name === SYSTEM_PROMPT_SUGGESTED_PROMPTS_ALL_HOUSEHOLDS)
              ?.config?.maximizer?.suggestedPrompts ?? [],
        ),
        map((suggested) => toAllHouseholdsStarterPrompts(suggested)),
        catchError(() => of([] as IQBoostStarterPrompt[])),
        tap((prompts) => this.prompts.set(prompts)),
        finalize(() => this.loadingPrompts.set(false)),
      );
  }
}
